import { useContext } from 'react';

import type { PrepareEthDeposit, Transaction } from '@sorare/wallet-shared';
import { MessagingContext } from '@sorare/wallet-shared';
import type { Data } from 'components/Form';
import { ControlledForm, useFormData } from 'components/Form';
import InfoContext from 'contexts/info';
import { gWeiToWei } from 'lib/utils';
import type Wallet from 'lib/wallet';
import { AmountWithConversion } from 'src/components/AmountWithConversion';

interface Props {
  depositId?: string;
  wallet: Wallet;
  balance: bigint;
  gas: bigint;
  gasPrice: bigint;
  onSuccess: () => void;
}

const fields = [
  [
    'gasPrice',
    'number',
    { placeholder: 'gasPricePlaceholder', step: 'any', min: 0, max: 500 },
  ],
] as const;

type Field = (typeof fields)[number][0];

const DepositBaseBankForm = ({
  depositId,
  wallet,
  balance,
  gas,
  gasPrice: proposedGasPrice,
  onSuccess,
}: Props) => {
  const initialValues = {
    gasPrice: (Number(proposedGasPrice) / 1e9)
      .toFixed(9)
      .replace(/(\.[0-9]+[1-9])0+$/, '$1'),
  };

  const [data, setData] = useFormData<Field>(fields, initialValues);
  const { sendRequest } = useContext(MessagingContext)!;
  const {
    dict,
    info: { fiatCurrencySymbol, ethRate },
  } = useContext(InfoContext)!;

  const { ethereumWallet, starkwareWallet } = wallet;

  const txInfo = (gasPriceInGwei: string) => {
    const gasPrice = gWeiToWei(gasPriceInGwei);
    const fee = gasPrice * BigInt(gas);
    const totalFees = fee + gasPrice * 21_000n;
    const value = BigInt(balance) - totalFees;
    const balanceBI = BigInt(balance);

    return { value, gasPrice, balanceBI, totalFees };
  };

  const onSubmit = async ({ gasPrice: gasPriceInWei }: Data<Field>) => {
    if (starkwareWallet && ethereumWallet) {
      const sorareAddress = ethereumWallet.address;
      const { value, gasPrice } = txInfo(gasPriceInWei);

      try {
        const { result } = await sendRequest<PrepareEthDeposit>(
          'prepareEthDeposit',
          {
            weiAmount: value,
          }
        );
        if (!result) return { gasPrice: 'unable to fetch deposit payload' };

        const tx = {
          ...ethereumWallet.depositTransaction(sorareAddress),
          value: result.weiAmount.toString(),
          gasPrice,
          gas: gas + 21_000n,
        };

        const hash = await ethereumWallet.sendTransaction(tx);
        await sendRequest<Transaction>('transaction', {
          hash,
          depositId,
        });

        onSuccess();
        return undefined;
      } catch (error) {
        return { gasPrice: (error as Error).message };
      }
    } else {
      return undefined;
    }
  };

  const { totalFees, value, balanceBI } = txInfo(data.gasPrice);

  const canDeposit = !(value < 0n);

  return (
    <>
      <div className="summary-table">
        <div className="summary-line">
          <span>{dict.availableAmountForDeposit}</span>
          <AmountWithConversion
            fiatCurrencySymbol={fiatCurrencySymbol}
            ethRate={ethRate}
            value={balanceBI}
          />
        </div>
        {!!canDeposit && (
          <>
            <div className="summary-line">
              <span>{dict.transactionCost}</span>
              <AmountWithConversion
                fiatCurrencySymbol={fiatCurrencySymbol}
                ethRate={ethRate}
                value={totalFees}
              />
            </div>

            <div className="summary-line" style={{ fontWeight: 600 }}>
              <span>{dict.depositAmount}</span>
              <AmountWithConversion
                fiatCurrencySymbol={fiatCurrencySymbol}
                ethRate={ethRate}
                value={value}
              />
            </div>
          </>
        )}
      </div>
      <span>{dict.depositAvailableSoon}</span>
      {canDeposit ? (
        <ControlledForm
          inputs={fields}
          formData={data}
          onSubmit={onSubmit}
          onSuccess={onSuccess}
          onChange={setData}
          submitLabel={dict.depositFundsSubmit}
          hideForm
        />
      ) : (
        <div className="helper">{dict.insufficientFundsForDeposit}</div>
      )}
    </>
  );
};

export default DepositBaseBankForm;
